import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useTeamStore } from '../store/teamStore';
import type { Play } from '../types';
import { BookOpen, Plus, Edit2, Trash2 } from 'lucide-react';

const PlaybookManagement: React.FC = () => {
  const { currentTeam } = useTeamStore();
  const [plays, setPlays] = useState<Play[]>([]);
  const [isAddingPlay, setIsAddingPlay] = useState(false);
  const [editingPlay, setEditingPlay] = useState<Play | null>(null);
  const [selectedPlay, setSelectedPlay] = useState<Play | null>(null);

  const [playForm, setPlayForm] = useState({
    name: '',
    formation: '',
    type: 'Offense',
    description: '',
    diagram: '',
  });

  useEffect(() => {
    if (currentTeam) {
      fetchPlays();
    }
  }, [currentTeam]);

  const fetchPlays = async () => {
    const { data, error } = await supabase
      .from('plays')
      .select('*')
      .eq('team_id', currentTeam?.id)
      .order('created_at');

    if (error) {
      console.error('Error fetching plays:', error);
      return;
    }

    setPlays(data as Play[]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const playData = {
      ...playForm,
      team_id: currentTeam?.id,
    };

    if (editingPlay) {
      const { error } = await supabase
        .from('plays')
        .update(playData)
        .eq('id', editingPlay.id);

      if (error) {
        console.error('Error updating play:', error);
        return;
      }
    } else {
      const { error } = await supabase
        .from('plays')
        .insert([playData]);

      if (error) {
        console.error('Error adding play:', error);
        return;
      }
    }

    setPlayForm({
      name: '',
      formation: '',
      type: 'Offense',
      description: '',
      diagram: '',
    });
    setIsAddingPlay(false);
    setEditingPlay(null);
    fetchPlays();
  };

  const handleEdit = (play: Play) => {
    setEditingPlay(play);
    setPlayForm({
      name: play.name,
      formation: play.formation,
      type: play.type,
      description: play.description,
      diagram: play.diagram,
    });
    setIsAddingPlay(true);
  };

  const handleDelete = async (playId: string) => {
    const { error } = await supabase
      .from('plays')
      .delete()
      .eq('id', playId);

    if (error) {
      console.error('Error deleting play:', error);
      return;
    }

    if (selectedPlay?.id === playId) {
      setSelectedPlay(null);
    }
    fetchPlays();
  };

  if (!currentTeam) {
    return <div>Please set up your team first.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-green-800">Playbook Management</h2>
        <button
          onClick={() => setIsAddingPlay(true)}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Play
        </button>
      </div>

      {isAddingPlay && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4">
            {editingPlay ? 'Edit Play' : 'Add New Play'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Play Name
              </label>
              <input
                type="text"
                value={playForm.name}
                onChange={(e) => setPlayForm({ ...playForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Formation
              </label>
              <input
                type="text"
                value={playForm.formation}
                onChange={(e) => setPlayForm({ ...playForm, formation: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={playForm.type}
                onChange={(e) => setPlayForm({ ...playForm, type: e.target.value as 'Offense' | 'Defense' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="Offense">Offense</option>
                <option value="Defense">Defense</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={playForm.description}
                onChange={(e) => setPlayForm({ ...playForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Diagram URL
              </label>
              <input
                type="url"
                value={playForm.diagram}
                onChange={(e) => setPlayForm({ ...playForm, diagram: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="col-span-2 flex justify-end gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsAddingPlay(false);
                  setEditingPlay(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
              >
                {editingPlay ? 'Update Play' : 'Add Play'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {plays.map((play) => (
          <div
            key={play.id}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all ${
              selectedPlay?.id === play.id ? 'ring-2 ring-green-500' : ''
            }`}
            onClick={() => setSelectedPlay(play)}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold">{play.name}</h3>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(play);
                  }}
                  className="text-green-600 hover:text-green-900"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(play.id);
                  }}
                  className="text-red-600 hover:text-red-900"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">
              {play.formation} | {play.type}
            </div>
            <p className="text-gray-700 mb-4">{play.description}</p>
            <a
              href={play.diagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-800 text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              View Diagram
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlaybookManagement;